import React, { useState, useEffect } from 'react';
import settingsService, { MotifRefoulement } from '@/services/settingsService';

interface MotifRefoulementDetailsProps {
  motifId: string;
  className?: string;
  showHeader?: boolean;
}

const MotifRefoulementDetails: React.FC<MotifRefoulementDetailsProps> = ({
  motifId,
  className = '',
  showHeader = true
}) => {
  const [motif, setMotif] = useState<MotifRefoulement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (motifId) {
      loadMotif();
    }
  }, [motifId]);

  const loadMotif = async () => {
    try {
      setLoading(true);
      setError('');
      
      const motifs = await settingsService.getMotifsRefoulement();
      const foundMotif = motifs.find(m => m.id === motifId);
      
      if (foundMotif) {
        setMotif(foundMotif);
      } else {
        setError('Motif de refoulement non trouvé');
      }
    } catch (err) {
      console.error('Erreur lors du chargement du motif:', err);
      setError('Erreur lors du chargement du motif');
    } finally {
      setLoading(false);
    }
  };

  const getCriticiteColor = (criticite: string) => {
    switch (criticite) {
      case 'Critique':
        return 'text-red-600 bg-red-100 border-red-200';
      case 'Haute':
        return 'text-orange-600 bg-orange-100 border-orange-200';
      case 'Moyenne':
        return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'Faible':
        return 'text-green-600 bg-green-100 border-green-200';
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getCriticiteIcon = (criticite: string) => {
    switch (criticite) {
      case 'Critique':
        return 'fas fa-exclamation-triangle';
      case 'Haute':
        return 'fas fa-exclamation-circle';
      case 'Moyenne':
        return 'fas fa-info-circle';
      case 'Faible':
        return 'fas fa-check-circle';
      default:
        return 'fas fa-info';
    }
  };

  if (loading) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mr-2"></div>
          <span className="text-gray-500">Chargement du motif...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className}`}>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <i className="fas fa-exclamation-triangle text-red-500 mr-2"></i>
            <span className="text-red-600">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!motif) {
    return (
      <div className={`${className}`}>
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center">
            <i className="fas fa-info-circle text-gray-500 mr-2"></i>
            <span className="text-gray-600">Aucun motif sélectionné</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
      {showHeader && (
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Détails du motif de refoulement</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">{motif.code}</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getCriticiteColor(motif.criticite)}`}>
                <i className={`${getCriticiteIcon(motif.criticite)} mr-1`}></i>
                {motif.criticite}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="space-y-4">
          {/* Titre du motif */}
          <div>
            <h4 className="text-xl font-semibold text-gray-900 mb-2">{motif.libelle}</h4>
            <p className="text-gray-600 leading-relaxed">{motif.description}</p>
          </div>

          {/* Informations détaillées */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center mb-2">
                <i className="fas fa-tag text-gray-500 mr-2"></i>
                <span className="text-sm font-medium text-gray-700">Catégorie</span>
              </div>
              <p className="text-gray-900 font-medium">{motif.categorie}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center mb-2">
                <i className="fas fa-hashtag text-gray-500 mr-2"></i>
                <span className="text-sm font-medium text-gray-700">Code</span>
              </div>
              <p className="text-gray-900 font-mono text-sm">{motif.code}</p>
            </div>
          </div>

          {/* Niveau de criticité */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center mb-2">
              <i className="fas fa-shield-alt text-gray-500 mr-2"></i>
              <span className="text-sm font-medium text-gray-700">Niveau de criticité</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getCriticiteColor(motif.criticite)}`}>
                <i className={`${getCriticiteIcon(motif.criticite)} mr-1`}></i>
                {motif.criticite}
              </span>
              <span className="text-sm text-gray-600">
                {motif.criticite === 'Critique' && 'Nécessite une action immédiate'}
                {motif.criticite === 'Haute' && 'Nécessite une attention prioritaire'}
                {motif.criticite === 'Moyenne' && 'Nécessite une attention normale'}
                {motif.criticite === 'Faible' && 'Peut être traité en routine'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MotifRefoulementDetails;
